import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axiosInstance from '../api/axios';
import useCartStore from '../store/cartStore';
import { expectedDeliveryFromItems, formatGermanDate } from '../utils/deliveryTime';
import useAuthStore from '../store/authStore';
import { imgVariant } from '../utils/imgUrl';
import { formatEUR } from '../utils/format';

const Cart = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, getTotal } = useCartStore();
  const { isAuthenticated } = useAuthStore();
  const [reminderEmail, setReminderEmail] = useState('');
  const [reminderSaved, setReminderSaved] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [shipping, setShipping] = useState(null);

  useEffect(() => {
    if (items.length === 0) return;
    axiosInstance
      .post('/shipping/calculate', { product_ids: items.map((i) => i.product_id), subtotal: getTotal() })
      .then((res) => setShipping(res.data))
      .catch(() => {
        const free = getTotal() >= 30;
        setShipping({ shipping_cost: free ? 0 : 3.99, remaining_for_free_shipping: free ? 0 : 30 - getTotal() });
      });
    // eslint-disable-next-line
  }, [items]);

  const handleCheckout = () => {
    // Allow guest checkout - go directly to checkout page
    navigate('/checkout');
  };

  const handleSaveForLater = async () => {
    const email = reminderEmail.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error('Bitte gib eine gültige Email-Adresse ein');
      return;
    }
    setReminderBusy(true);
    try {
      await axiosInstance.post('/cart/save-for-later', {
        email,
        items: items.map((it) => ({
          product_id: it.id || it.product_id,
          quantity: it.quantity,
          price: it.price,
          selected_color: it.selected_color || null,
        })),
      });
      setReminderSaved(true);
      toast.success('Warenkorb gespeichert! Wir erinnern dich morgen, falls nötig.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setReminderBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="text-center">
          <ShoppingBag className="h-24 w-24 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="empty-cart-title">
            Ihr Warenkorb ist leer
          </h2>
          <p className="text-gray-600 mb-6">Fügen Sie Produkte hinzu, um fortzufahren</p>
          <Link to="/products">
            <Button data-testid="continue-shopping">Weiter einkaufen</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8" data-testid="cart-title">Warenkorb</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <div
                key={item.product_id}
                className="bg-white rounded-lg shadow-md p-6"
                data-testid={`cart-item-${item.product_id}`}
              >
                <div className="flex gap-6">
                  {/* Product Image */}
                  <Link to={`/products/${item.product_id}`} className="flex-shrink-0">
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100">
                      {item.product?.images && item.product.images.length > 0 ? (
                        <img
                          src={imgVariant(item.product.images[0], { w: 160 })}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </Link>
                  
                  {/* Product Details */}
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <div>
                        <Link
                          to={`/products/${item.product_id}`}
                          className="text-lg font-semibold text-gray-900 hover:text-blue-600"
                        >
                          {item.product?.name || 'Produkt'}
                        </Link>
                        {item.selected_color && (
                          <p className="text-sm text-gray-700 mt-1">
                            <span className="font-medium">Farbe:</span> {item.selected_color}
                          </p>
                        )}
                        {item.gift_label_text && (
                          <p className="text-sm mt-1 text-[#5D4E3C]" data-testid={`cart-item-gift-label-${item.product_id}`}>
                            <span className="font-medium" style={{ color: '#C4A084' }}>♡ Etikett:</span> „{item.gift_label_text}"
                          </p>
                        )}
                        {item.gift_note && (
                          <p className="text-xs mt-1 text-amber-700" data-testid={`cart-item-gift-note-${item.product_id}`}>
                            <span className="font-medium">📝 Bemerkung:</span> {item.gift_note}
                          </p>
                        )}
                        <p className="text-xs text-[#8B7355] mt-1" data-testid={`cart-item-delivery-${item.product_id}`}>
                          🚚 Lieferzeit: {(item.product?.delivery_time === '3-7') ? '3–7 Werktage · wird auf Bestellung gefertigt' : '1–3 Werktage · auf Lager'}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {formatEUR(item.price)} pro Stück
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(item.line_id || item.product_id)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                    
                    {/* Quantity Controls */}
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.line_id || item.product_id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          data-testid={`decrease-${item.product_id}`}
                          className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="text-lg font-semibold w-12 text-center" data-testid={`quantity-${item.product_id}`}>
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.line_id || item.product_id, item.quantity + 1)}
                          data-testid={`increase-${item.product_id}`}
                          className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-lg font-bold text-gray-900">
                        {formatEUR((item.price * item.quantity))}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-testid="order-summary-title">
                Bestellzusammenfassung
              </h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Zwischensumme</span>
                  <span data-testid="subtotal">{formatEUR(getTotal())}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Versand</span>
                  <span data-testid="shipping">
                    {!shipping ? '…' : shipping.shipping_cost === 0 ? 'Kostenlos' : formatEUR(shipping.shipping_cost)}
                  </span>
                </div>
                <div className="border-t pt-3 flex justify-between text-lg font-bold">
                  <span>Gesamt</span>
                  <span data-testid="total">
                    {formatEUR((getTotal() + (shipping?.shipping_cost || 0)))}
                  </span>
                </div>
              </div>
              
              {shipping?.shipping_cost > 0 && shipping.remaining_for_free_shipping > 0 && (
                <p className="text-sm text-gray-600 mb-2" data-testid="free-shipping-hint">
                  Noch {formatEUR(shipping.remaining_for_free_shipping)} bis zum kostenlosen Versand!
                </p>
              )}

              {/* Gesamt-Lieferzeit: das langsamste Produkt bestimmt den Versand */}
              {items.some(it => it.product?.delivery_time === '3-7') ? (
                <div className="text-xs bg-amber-50 border border-amber-200 px-3 py-2 mb-2 text-amber-900" data-testid="cart-overall-delivery">
                  🚚 <strong>Lieferzeit gesamt: 3–7 Werktage</strong> · einer deiner Artikel wird handgemacht auf Bestellung gefertigt.
                </div>
              ) : (
                <p className="text-xs text-[#5D8A5A] mb-2" data-testid="cart-overall-delivery">
                  🚚 <strong>Lieferzeit: 1–3 Werktage</strong> · alle Artikel sind auf Lager.
                </p>
              )}

              {/* Voraussichtliches Lieferdatum (frühestens) */}
              {items.length > 0 && (
                <p className="text-xs text-[#5D4E3C] mb-4 px-3 py-2 bg-[#FAF5EE] border border-[#E8DCC8]" data-testid="cart-expected-delivery-date">
                  📅 Lieferung voraussichtlich am <strong>{formatGermanDate(expectedDeliveryFromItems(new Date(), items))}</strong>
                </p>
              )}
              
              <Button
                onClick={handleCheckout}
                className="w-full bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white hover:opacity-90"
                size="lg"
                data-testid="checkout-button"
              >
                Zur Kasse gehen
              </Button>
              
              {!isAuthenticated && (
                <p className="text-sm text-gray-600 text-center mt-3">
                  Sie können auch als Gast bestellen - ohne Anmeldung! 🛒
                </p>
              )}

              {!isAuthenticated && items.length > 0 && (
                <div className="mt-4 p-3 border border-[#E8DCC8] bg-[#FAF5EE]" data-testid="cart-save-for-later">
                  {reminderSaved ? (
                    <p className="text-sm text-[#5D4E3C] flex items-center justify-center gap-2">
                      <Check className="h-4 w-4 text-green-600" />
                      <span>Wir erinnern dich morgen — falls du dann noch nicht bestellt hast.</span>
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-[#6D5A47] mb-2 flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 text-[#C4A084]" />
                        <strong>Warenkorb für später speichern?</strong> (optional)
                      </p>
                      <div className="flex gap-2 mb-2">
                        <Input
                          type="email"
                          placeholder="deine@email.de"
                          value={reminderEmail}
                          onChange={(e) => setReminderEmail(e.target.value)}
                          className="flex-1 text-sm h-9"
                          data-testid="cart-reminder-email-input"
                          disabled={reminderBusy}
                        />
                        <Button
                          onClick={handleSaveForLater}
                          disabled={reminderBusy || !reminderEmail}
                          size="sm"
                          variant="outline"
                          className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#5D4E3C] hover:text-white shrink-0"
                          data-testid="cart-reminder-save-btn"
                        >
                          {reminderBusy ? '…' : 'Speichern'}
                        </Button>
                      </div>
                      <p className="text-[10px] text-[#8B7355] leading-snug">
                        Mit dem Klick auf &bdquo;Speichern&ldquo; stimmst du zu, dass wir deine Email-Adresse einmalig
                        verwenden dürfen, um dich an deinen Warenkorb zu erinnern (eine Email, kein Spam, keine Werbung).
                        Widerruf jederzeit über den Link in der Email möglich.{' '}
                        <Link to="/datenschutz" className="underline hover:text-[#5D4E3C]" target="_blank" rel="noreferrer">
                          Datenschutzerklärung
                        </Link>
                      </p>
                    </>
                  )}
                </div>
              )}

              <Link to="/products" className="block mt-4">
                <Button variant="outline" className="w-full rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]" data-testid="continue-shopping-button">
                  Weiter einkaufen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;